import React, { useState, useEffect } from 'react';
import { pointsAdminService, UserPointsData, PointTransaction } from '../../services/pointsAdminService';
import UserSearchForm from './UserSearchForm';
import UserPointsDetails from './UserPointsDetails';
import PointsAdjustmentModal from './PointsAdjustmentModal';
import PlanManagement from './PlanManagement';

interface UserPointsManagerProps {
  onError: (error: string) => void;
}

const UserPointsManager: React.FC<UserPointsManagerProps> = ({ onError }) => {
  const [searchResults, setSearchResults] = useState<UserPointsData[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserPointsData | null>(null);
  const [userTransactions, setUserTransactions] = useState<PointTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);

  const handleUserSearch = async (query: string, plan?: string) => {
    try {
      setLoading(true);
      const results = await pointsAdminService.searchUsers(query, plan);
      setSearchResults(results);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (user: UserPointsData) => {
    try {
      setLoading(true);
      const [userDetails, transactions] = await Promise.all([
        pointsAdminService.getUserPoints(user.userId),
        pointsAdminService.getUserTransactions(user.userId)
      ]);
      
      setSelectedUser(userDetails);
      setUserTransactions(transactions);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handlePointsAdjustment = async (amount: number, reason: string) => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      await pointsAdminService.adjustUserPoints(selectedUser.userId, amount, reason);
      
      // Refresh user data
      await handleUserSelect(selectedUser);
      setShowAdjustmentModal(false);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Adjustment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPoints = async () => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      await pointsAdminService.resetUserPoints(selectedUser.userId);
      
      // Refresh user data
      await handleUserSelect(selectedUser);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (newPlan: string) => {
    if (!selectedUser) return;
    
    try {
      setLoading(true);
      await pointsAdminService.changePlan(selectedUser.userId, newPlan);
      
      // Refresh user data with updated plan
      const updatedUser = await pointsAdminService.getUserPoints(selectedUser.userId);
      const updatedTransactions = await pointsAdminService.getUserTransactions(selectedUser.userId);
      
      setSelectedUser(updatedUser);
      setUserTransactions(updatedTransactions);
      
      // Also refresh the search results if we're showing search results
      if (searchResults.length > 0) {
        // Update the user in search results
        setSearchResults(prev => 
          prev.map(user => 
            user.userId === selectedUser.userId 
              ? { ...user, plan: newPlan }
              : user
          )
        );
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Plan change failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ color: 'white', padding: '1.5rem', overflowX: 'auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ 
          fontSize: '1.5rem', 
          fontWeight: 'bold', 
          color: '#10b981',
          marginBottom: '0.5rem' 
        }}>
          👤 User Points Manager
        </h2>
        <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
          Search, manage, and adjust individual user points
        </p>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: selectedUser ? '1fr 1fr' : '1fr', 
        gap: '1.5rem' 
      }}>
        {/* Search Section */}
        <div>
          <UserSearchForm
            onSearch={handleUserSearch}
            loading={loading}
          />
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <UserSearchResults
              users={searchResults}
              onUserSelect={handleUserSelect}
              selectedUserId={selectedUser?.userId}
              loading={loading}
            />
          )}
        </div>

        {/* User Details Section */}
        {selectedUser && (
          <div>
            {/* Plan Management */}
            <PlanManagement
              user={selectedUser}
              onPlanChange={handlePlanChange}
              loading={loading}
            />
            
            {/* Info Banner for Non-Points Users */}
            {selectedUser.plan === 'creator' && (
              <div style={{ 
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                color: '#f59e0b'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  📋 Free Plan User
                </div>
                <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                  This user is on the free "creator" plan which uses feature counts (5 captions, 20 thumbnails, etc.) instead of points. 
                  Point adjustments will create transaction records but won't affect their actual feature usage limits.
                </div>
              </div>
            )}
            
            <UserPointsDetails
              user={selectedUser}
              transactions={userTransactions}
              onAdjustPoints={() => setShowAdjustmentModal(true)}
              onResetPoints={handleResetPoints}
              loading={loading}
            />
          </div>
        )}
      </div>

      {/* Adjustment Modal */}
      {showAdjustmentModal && (
        <PointsAdjustmentModal
          onConfirm={handlePointsAdjustment}
          onCancel={() => setShowAdjustmentModal(false)}
          loading={loading}
        />
      )}
    </div>
  );
};

interface UserSearchResultsProps {
  users: UserPointsData[];
  onUserSelect: (user: UserPointsData) => void;
  selectedUserId?: string;
  loading: boolean;
}

const UserSearchResults: React.FC<UserSearchResultsProps> = ({ 
  users, 
  onUserSelect, 
  selectedUserId, 
  loading 
}) => (
  <div style={{ 
    background: 'rgba(15, 23, 42, 0.8)', 
    border: '1px solid rgba(71, 85, 105, 0.3)',
    borderRadius: '12px',
    marginTop: '1rem'
  }}>
    <div style={{ 
      padding: '1rem', 
      borderBottom: '1px solid rgba(71, 85, 105, 0.3)' 
    }}>
      <h3 style={{ color: '#e5e7eb', fontSize: '1rem', margin: 0 }}>
        Search Results ({users.length})
      </h3>
    </div>
    
    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
      {users.map((user) => (
        <div
          key={user.userId}
          onClick={() => !loading && onUserSelect(user)}
          style={{
            padding: '1rem',
            borderBottom: '1px solid rgba(71, 85, 105, 0.2)',
            cursor: loading ? 'not-allowed' : 'pointer',
            backgroundColor: selectedUserId === user.userId ? 'rgba(6, 182, 212, 0.1)' : 'transparent',
            opacity: loading ? 0.6 : 1
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ color: '#e5e7eb', fontWeight: '500' }}>{user.email}</div>
              <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                {user.plan} • {user.role}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#06b6d4', fontWeight: '500' }}>
                {user.pointsRemaining.toLocaleString()} pts
              </div>
              <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                {user.isUnlimited ? 'Unlimited' : `/${user.monthlyPoints.toLocaleString()}`}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default UserPointsManager;
